<template>
  <b-modal
    id="biometric-validation-modal"
    :title="modalTitle"
    size="lg"
    @close="onCancel"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    hide-footer
    scrollable
  >
    <section id="biometric-steps" class="w-100">
      <div v-show="step === 0">
        <div class="w-100 d-flex align-items-center justify-content-center flex-column">
          <div class="feedback-icon mb-4 mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-camera" width="60" height="60" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2"></path>
              <path d="M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
            </svg>
          </div>
          <h4 class="text-uppercase h5">Validación biométrica</h4>
          <p class="mb-0">Vamos a validar la información de la firma mediante unas fotos.</p>
          <p>Necesitarás tener acceso a la cámara del dispositivo y contar con tu DNI.</p>
        </div>
      </div>

      <div v-show="step === 1">
        <div v-show="tempImage === null">
          <div class="video-wrapper d-flex align-items-center justify-content-center position-relative">
            <img class="overlay position-absolute" src="@/assets/images/user-silhouette.png" alt="selfie-photo">
            <video ref="video-recorder" width="100%" autoplay></video>
          </div>
        </div>

        <div v-show="tempImage !== null">
          <b-img-lazy thumbnail class="biometric-img" :src="imagePreviewSrc" alt="photo-preview"></b-img-lazy>
        </div>
      </div>

      <div v-show="step === 2">
        <div v-show="tempImage === null">
          <div class="video-wrapper d-flex align-items-center justify-content-center position-relative">
            <div class="scann-box">
              <span class="top"></span>
              <span class="bottom"></span>
            </div>
            <video ref="video-recorder-dni-front" width="100%" autoplay></video>
          </div>
        </div>

        <div v-show="tempImage !== null">
          <b-img-lazy thumbnail class="biometric-img" :src="imagePreviewSrc" alt="photo-preview"></b-img-lazy>
        </div>
      </div>

      <div v-show="step === 3">
        <div v-show="tempImage === null">
          <div class="video-wrapper d-flex align-items-center justify-content-center position-relative">
            <div class="scann-box">
              <span class="top"></span>
              <span class="bottom"></span>
            </div>
            <video ref="video-recorder-dni-back" width="100%" autoplay></video>
          </div>
        </div>

        <div v-show="tempImage !== null">
          <b-img-lazy thumbnail class="biometric-img" :src="imagePreviewSrc" alt="photo-preview"></b-img-lazy>
        </div>
      </div>

      <div v-show="step === 4">
        <div v-show="tempImage === null">
          <div class="video-wrapper d-flex align-items-center justify-content-center position-relative">
            <video ref="video-recorder-dni-selfie" width="100%" autoplay></video>
          </div>
        </div>

        <div v-show="tempImage !== null">
          <b-img-lazy thumbnail class="biometric-img" :src="imagePreviewSrc" alt="photo-preview"></b-img-lazy>
        </div>
      </div>

      <div v-if="uploadingImages">
        <div class="w-100 d-flex align-items-center justify-content-center flex-column">
          <div class="feedback-icon mb-4 mt-2 d-inline-block">
            <a-icon type="loading" :style="{ 'font-size': '4rem' }" />
          </div>
          <h4 class="text-uppercase h5">Un momento por favor...</h4>
        </div>
      </div>

      <div v-if="uploadCompleted" class="d-flex flex-column justify-content-center align-items-center">
        <div class="feedback-icon mb-4 mt-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-cloud-upload" width="60" height="60" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
            <path d="M9 15l3 -3l3 3"></path>
            <path d="M12 12l0 9"></path>
          </svg>
        </div>
        <h4 class="h5 text-uppercase">¡Genial!</h4>
        <p>Puede continuar con el proceso de firma</p>
      </div>

      <div
        class="d-flex align-items-center justify-content-center mt-4 mb-3"
        style="gap: .75rem"
      >
        <template v-if="step === 0">
          <a-button
            size="large"
            type="default"
            @click="onCancel"
          >
            Lo haré luego
          </a-button>
          <a-button
            size="large"
            type="primary"
            @click="step++"
          >
            Continuar
          </a-button>
        </template>
        <template v-if="step > 0 && step < 5">
          <a-button
            v-if="tempImage === null"
            size="large"
            type="primary"
            @click="onSnap"
          >
            Tomar Foto
          </a-button>
          <div v-else class="d-flex" style="gap: .75rem">
            <a-button
              size="large"
              type="default"
              @click="tempImage = null"
            >
              Volver a tomar
            </a-button>
            <a-button
              size="large"
              type="primary"
              @click="onConfirm"
            >
              Confirmar
            </a-button>
          </div>
        </template>
        <a-button v-if="uploadCompleted" size="large" type="primary" @click="onContinue">Continuar</a-button>
      </div>
    </section>
  </b-modal>
</template>

<script>
import commonsApi from '@/api/commons'

export default {
  data () {
    return {
      step: 0,
      imagePreviewSrc: null,
      tempImage: null,
      dataImages: [],
      uploadingImages: false,
      uploadCompleted: false
    }
  },
  computed: {
    modalTitle () {
      switch (this.step) {
        case 1:
          return 'Tomate una foto'
        case 2:
          return 'Toma una foto del frente de tu DNI'
        case 3:
          return 'Toma una foto del dorso de tu DNI'
        case 4:
          return 'Tomate una foto junto a tu DNI'
        default:
          return 'Validación biométrica'
      }
    }
  },
  watch: {
    step (val) {
      if (val > 0 && val < 5) {
        this.initBiometricValidation()
      } else {
        this.stopRecording()
      }
    }
  },
  methods: {
    show () {
      this.$bvModal.show('biometric-validation-modal')
    },

    async initBiometricValidation () {
      try {
        const video = this.$refs['video-recorder']
        const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
        video.srcObject = stream
        window.stream = stream
      } catch (error) {
        console.log(error)
      }
    },

    async uploadBiometricFiles () {
      try {
        this.uploadingImages = true
        const images = new FormData()
        for (const file of this.dataImages) {
          images.append('file', file)
        }
        // Subo las imagenes + los datos del usuario y el timestamp
        await commonsApi.axiosPostWrapper('/upload-biometric-documentation', images, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.$route.params.token}`
          }
        })
        this.uploadCompleted = true
      } catch (error) {
        console.log(error)
      } finally {
        this.uploadingImages = false
      }
    },

    urltoFile (url, filename, mimeType) {
      return (fetch(url)
        .then(function (res) {
          return res.arrayBuffer()
        })
        .then(function (buf) {
          return new File([buf], filename, { type: mimeType })
        })
      )
    },

    async onSnap () {
      const video = this.$refs['video-recorder']
      const canvas = document.createElement('canvas')
      canvas.width = 1920
      canvas.height = 1080
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      const image = canvas.toDataURL('image/jpg')

      const file = await this.urltoFile(image, 'biometric-documentation.jpg', 'image/jpg')
      this.tempImage = file
      this.imagePreviewSrc = URL.createObjectURL(file)
    },

    async onConfirm () {
      this.dataImages.push(this.tempImage)
      this.tempImage = null
      this.step++
      this.$nextTick(async () => {
        let video
        if (this.step === 2) {
          video = this.$refs['video-recorder-dni-front']
        } else if (this.step === 3) {
          video = this.$refs['video-recorder-dni-back']
        } else if (this.step === 4) {
          video = this.$refs['video-recorder-dni-selfie']
        } else if (this.step === 5) {
          this.stopRecording()
          return this.uploadBiometricFiles()
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
        video.srcObject = stream
        window.stream = stream
      })
    },

    stopRecording () {
      const videos = document.querySelectorAll('video')
      for (const video of videos) {
        const stream = video.srcObject
        if (stream) {
          const tracks = stream.getTracks()
          tracks.forEach(track => {
            track.stop()
          })
          video.srcObject = null
        }
      }
    },

    onContinue () {
      this.$emit('ok', this.dataImages)
      this.onCancel()
    },

    onCancel () {
      this.stopRecording()
      this.step = 0
      this.imagePreviewSrc = null
      this.tempImage = null
      this.dataImages = []
      this.uploadCompleted = false

      this.$bvModal.hide('biometric-validation-modal')
    }
  },
  beforeDestroy () {
    this.stopRecording()
  }
}
</script>

<style scoped>
.feedback-icon {
  padding: 1rem;
  border: 4px solid #0acf97;
  border-radius: 20px;
  color: #0acf97;
}

.biometric-img {
  width: 400px;
  height: 300px;
  display: block;
  margin: 0 auto;
}

.video-wrapper {
  width: 400px;
  height: 400px;
  background: black;
  margin: 0 auto;
}
.overlay {
  margin: 0;
  border: 20px solid black;
  width: 400px;
  height: 400px;
}

.scann-box p::before {
  content: '';
  display:inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #18c89b;
  position: relative;
  right: 4px;
}

.scann-box {
  width: 360px;
  height: 225px;
  position: absolute;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  backface-visibility: hidden;
}

.scann-box:before,
.scann-box:after,
.scann-box span:after,
.scann-box span:before {
  border-color: #18c89b;
  content: "";
  position: absolute;
  width: 45px;
  height: 46px;
  border-style: solid;
  border-width: 0px;
}
.scann-box:before {
  content:"";
  top:0;
  bottom:0;
  left:0;
  width:18px;
  height: 100%;
  background:#18c89b;
  box-shadow:0 0 70px 20px #18c89b;
  clip-path:inset(0);
  animation:
    x 1s ease-in-out infinite alternate,
    y 2s   ease-in-out infinite;
}
.scann-box span.bottom:before {
  left: 0;
  bottom: 0;
  border-left-width: 5px;
  border-bottom-width: 5px;
  border-radius: 0 0 0 5px;
}
.scann-box span.bottom:after {
  right: 0;
  bottom: 0;
  border-right-width: 5px;
  border-bottom-width: 5px;
  border-radius: 0 0 5px 0;
}
.scann-box span.top:before {
  left: 0;
  top: 0;
  border-left-width: 5px;
  border-top-width: 5px;
  border-radius: 5px 0 0 0;
}
.scann-box span.top:after {
  right: 0;
  top: 0;
  border-right-width: 5px;
  border-top-width: 5px;
  border-radius: 0 5px 0 0;
}

@keyframes x {
  to {
    transform:translateX(-100%);
    left:100%;
  }
}

@keyframes y {
   33% {
     clip-path:inset(0 0 0 -100px);
   }
   50% {
     clip-path:inset(0 0 0 0);
   }
   83%{
     clip-path:inset(0 -100px 0 0);
   }
}
</style>
